
import { Component, Vue } from 'vue-property-decorator';
import { Guid } from 'guid-typescript';
import {
  Client,
  IJobItemModel,
  JobItemModel,
  JobItemUpdateScheduleModel,
  EmployeeModel
} from '@/services/api.service';
import { EventBus } from '@/event-bus';
import { JobItemCreatedMessage } from '@/services/eventbus.models';
import * as DateUtils from '@/utilities/date.utils';
import * as StringUtils from '@/utilities/string.utils';

interface DateEntry {
  date: string;
  timeRange: string;
  assignedEmployeeGid: string;
}

@Component({
  props: {
    jobItem: {
      type: Object as () => IJobItemModel,
      required: true
    },
    jobGid: String,
    isInDialogMode: Boolean
  }
})
export default class RepeatJobItem extends Vue {
  private itemName: string = '';
  private originalJobItem: IJobItemModel = {} as IJobItemModel;
  private scheduledDates: DateEntry[] = [];
  private assignableEmployees: EmployeeModel[] = [];
  private isSaving: boolean = false;
  private saveProgress: number = 0;

  private mounted() {
    this.originalJobItem = this.$props.jobItem;
    this.itemName = this.$props.jobItem.name;

    // Add one initial date entry
    this.addDateEntry();

    // Load assignable employees
    this.updateAssignableEmployees(this.$props.jobItem.jobItemType);
  }

  private get canSave(): boolean {
    return this.scheduledDates.length > 0 && 
           this.scheduledDates.every(entry => entry.date !== '');
  }

  private get hasDuplicateDates(): boolean {
    const dates = this.scheduledDates.map(entry => entry.date).filter(date => date !== '');
    return dates.length !== new Set(dates).size;
  }

  private addDateEntry() {
    let nextDate: Date;
    
    // Find the maximum date from existing scheduled dates
    const validDates = this.scheduledDates
      .map(entry => entry.date)
      .filter(date => date !== '')
      .map(dateStr => new Date(dateStr));
    
    if (validDates.length > 0) {
      // Get the maximum date and add 1 day
      const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));
      nextDate = new Date(maxDate);
      nextDate.setDate(maxDate.getDate() + 1);
    } else {
      // Fallback to next working day if no dates exist
      nextDate = this.getNextWorkingDay();
    }
    
    this.scheduledDates.push({
      date: DateUtils.toIso8601Date(nextDate),
      timeRange: '',
      assignedEmployeeGid: ''
    });
  }

  private removeDateEntry(index: number) {
    this.scheduledDates.splice(index, 1);
  }

  private async createRepeatedJobItems() {
    if (!this.canSave || this.isSaving) {
      return;
    }

    // Set loading state and reset progress
    this.isSaving = true;
    this.saveProgress = 0;

    // @ts-ignore
    this.$Progress.start();

    const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
    const totalItems = this.scheduledDates.length;
    
    try {
      // Create each job item
      for (let index = 0; index < this.scheduledDates.length; index++) {
        const dateEntry = this.scheduledDates[index];
        
        // Clone the original job item
        const newJobItem: JobItemModel = new JobItemModel();
        const newGid = Guid.create().toString();
        
        newJobItem.init({
          gid: newGid,
          jobGid: this.$props.jobGid,
          name: this.originalJobItem.name,
          description: this.originalJobItem.description,
          jobItemType: this.originalJobItem.jobItemType,
          serviceTypeGid: this.originalJobItem.serviceTypeGid,
          status: this.originalJobItem.status,
          expectedStartDate: this.originalJobItem.expectedStartDate,
          locationGid: this.originalJobItem.locationGid,
          isFullDay: this.originalJobItem.isFullDay,
          purchaseOrderNumber: this.originalJobItem.purchaseOrderNumber,
          contactName: this.originalJobItem.contactName,
          contactPhoneNumber: this.originalJobItem.contactPhoneNumber,
          quoteValue: this.originalJobItem.quoteValue,
          isAdditionalToQuotedProjectScope: this.originalJobItem.isAdditionalToQuotedProjectScope
        });

        // Create the job item
        const createdJobItem = await clientService.createJobItem(this.$props.jobGid, newJobItem);

        // Create schedule if date is provided
        if (dateEntry.date) {
          const updateModel: JobItemUpdateScheduleModel = new JobItemUpdateScheduleModel();
          updateModel.jobItemScheduleGid = Guid.create().toString();
          // Convert the date string to a Date object, then to ISO number format
          updateModel.scheduledDateISO = DateUtils.toIsoDate(new Date(dateEntry.date));
          updateModel.scheduledTimeRange = dateEntry.timeRange;
          updateModel.estimatedDurationInMinutes = 0;
          updateModel.estimatedTravelTimeInMinutes = 0;
          updateModel.assignedToEmployeeGid = dateEntry.assignedEmployeeGid || undefined;

          await clientService.scheduleJobItem(this.$props.jobGid, createdJobItem.gid!, updateModel);
        }

        // Emit event for each created job item
        EventBus.$emit('job-item-created', new JobItemCreatedMessage(this.$props.jobGid, createdJobItem));

        // Update progress after each job item is created
        this.saveProgress = Math.round(((index + 1) / totalItems) * 100);
      }

      // @ts-ignore
      this.$Progress.finish();
      // @ts-ignore
      this.$awn.success(`Successfully created ${this.scheduledDates.length} job item${this.scheduledDates.length !== 1 ? 's' : ''}`);
      
      this.$emit('job-items-repeated');
      this.closeModal();
    } catch (error) {
      // @ts-ignore
      this.$Progress.fail();
      // @ts-ignore
      this.$awn.alert('Failed to create repeated job items.\r\n' + error.message);
    } finally {
      // Reset loading state
      this.isSaving = false;
      this.saveProgress = 0;
    }
  }

  private cancelEdit() {
    this.closeModal();
  }

  private closeModal() {
    if (this.$props.isInDialogMode) {
      this.$modal.hide('modal-jobitem-repeat');
    }
  }

  private updateAssignableEmployees(jobItemType: number) {
    const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
    
    clientService.getAssignableEmployees(jobItemType as any).then(data => {
      this.assignableEmployees = data.sort((n1, n2) => 
        StringUtils.stringCompare(n1.firstName || '', n2.firstName || '')
      );
    });
  }

  private getNextWorkingDay(): Date {
    // Gets the next weekday from the current date
    const d = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(d.getDate() + 1);
    
    // 6 = Saturday, 0 = Sunday
    if (tomorrow.getDay() === 6) {
      tomorrow.setDate(tomorrow.getDate() + 2);
    }
    if (tomorrow.getDay() === 0) {
      tomorrow.setDate(tomorrow.getDate() + 1);
    }
    
    return tomorrow;
  }
}
